<template>
  <div :class="$style.page">
    <BaseContainer>
      <Modal
        :model-value="isVisible"
      >
        <form @submit.prevent="createDashboard">
          <FormBox
            :heading="formData.heading"
            :class="$style.noBorder"
          >
            <BaseLoader
              :class="$style.box"
              :is-active="isFormLoading"
            >
              <template v-if="true">
                <BaseField
                  :class="$style.field"
                >
                  <template #label>
                    DASHBOARD NAME
                  </template>

                  <BaseInput
                    v-model="dashboardName"
                    placeholder="Untitled"
                  />
                </BaseField>
                <BaseField
                  :class="$style.field"
                >
                  <template #label>
                    DESCRIPTION
                  </template>

                  <BaseInput
                    v-model="description"
                    placeholder="Some description"
                  />
                </BaseField>
                <BaseField
                  :class="$style.field"
                >
                  <template #label>
                    ICON
                  </template>

                  <BaseMultiselect
                    v-model="localModel.icon"
                    :options="iconOptions"
                    is-block
                  />
                </BaseField>
              </template>
            </BaseLoader>
            <template #footer>
              <BaseButton
                variant="success"
                type="createDashboard"
                is-block
              >
                {{ formData.submitText }}
              </BaseButton>
            </template>
          </FormBox>
        </form>
      </Modal>
      <NavigationCardsGrid>
        <NavigationCard
          v-for="dashboard in dashboards"
          :key="dashboard.id"
          color="aqua"
          :to="{ name: 'Dashboards/Charts', params: { dashboardId: dashboard.id } }"
        >
          <template #image>
            <component
              :is="$options.ICON[dashboard.icon].component"
              v-if="dashboard.icon"
            />
          </template>
          <template #title>
            {{ dashboard.name || 'Untitled' }}
          </template>
          <template #lead>
            {{ dashboard.description }}
          </template>
        </NavigationCard>
        <NavigationCard
          color="lime"
          is-dashed
          @click="showModal"
        >
          <template #image>
            <CardPlusSvg />
          </template>
          <template #title>
            Create New Dashboard
          </template>
          <template #lead>
            Monitor key insights with a new personalised dashboard.
          </template>
        </NavigationCard>
      </NavigationCardsGrid>
    </BaseContainer>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import gql from 'graphql-tag';
import { mapState } from 'vuex';
import BaseContainer from '@/components/base/Container';
import Modal from '@/components/base/Modal';
import BaseField from '@/components/base/Field';
import BaseInput from '@/components/base/Input';
import BaseButton from '@/components/base/Button';
import BaseMultiselect from '@/components/base/Multiselect';
import BaseLoader from '@/components/base/Loader';
import NavigationCardsGrid from '@/components/NavigationCardsGrid';
import NavigationCard from '@/components/NavigationCard';
import FormBox from '@/components/FormBox';
import CardPlusSvg from '@/assets/images/icons/card-plus.svg?inline';
import { ICON, ICONS } from '@/dashboards';

export default {
  components: {
    BaseContainer,
    Modal,
    BaseField,
    BaseInput,
    BaseButton,
    BaseMultiselect,
    BaseLoader,
    NavigationCardsGrid,
    NavigationCard,
    FormBox,
    CardPlusSvg,
  },
  FORM_DATA: {
    heading: 'Create new dashboard',
    submitText: 'Create',
  },
  ICON,
  apollo: {
    dashboards: {
      query: gql`
        query ($userId: String!) {
          dashboards(where: {owner_id: {_eq: $userId}}, order_by: {created_at: asc}) {
            id
            icon
            name
            description
          }
        }
      `,
      variables() {
        return {
          userId: this.user.username,
        };
      },
      skip() {
        return this.user.username == null;
      },
    },
  },
  data() {
    return {
      dashboards: [],
      isCreatingDashboard: false,
      dashboardName: '',
      description: '',
      iconOptions: [
        {
          label: 'None',
          value: null,
        },
        ...ICONS.map((icon) => ({
          value: icon.id,
          label: icon.label,
        })),
      ],
      isVisible: false,
      isFormLoading: false,
      localModel: {},
    };
  },
  computed: {
    ...mapState('user', ['user']),
    formData() {
      return this.$options.FORM_DATA;
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(value) {
        this.localModel = cloneDeep(value);
      },
    },
  },
  created() {
    this.$apollo.queries.dashboards.refetch();
  },
  methods: {
    updateLocalModel() {
      this.localModel = cloneDeep(this.modelValue);
    },
    showModal() {
      this.isVisible = true;
    },
    async createDashboard() {
      if (this.isCreatingDashboard) return;

      this.isCreatingDashboard = true;
      this.isFormLoading = true;

      const res = await this.$apollo.mutate({
        mutation: gql`mutation($userId: String!, $dashboardName: String!, $description: String!, $icon: Int!) {
          insert_dashboards_one(object: {
            owner_id: $userId, 
            name: $dashboardName, 
            description: $description, 
            icon: $icon
          }) {
            id
          }
        }`,
        variables: {
          userId: this.user.username,
          dashboardName: this.dashboardName,
          description: this.description,
          icon: this.localModel.icon || 1,
        },
      });

      await this.$router.push({
        name: 'Dashboards/Charts',
        params: { dashboardId: res.data.insert_dashboards_one.id },
      });

      this.isCreatingDashboard = false;
    },
  },
};
</script>

<style lang="scss" module>
.page {
  padding-top: map-get($page-vertical-paddings, 'regular');
  padding-bottom: map-get($page-vertical-paddings, 'regular');
}

.noBorder {
  border: 0;
}

.box {
  width: 470px;
}

.field {

  &:not(:first-child) {
    margin-top: 20px;
  }
}
</style>
